import { type FC } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { useCreateStandaloneAgentPackageLinkMutation } from '@features/integrations/api/create-agent.api';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  RadioGroup,
  RadioGroupItem,
} from '@shared/components/ui';
import { cn } from '@shared/utils/tailwind.utils';

import { cleanAgentDescription, handleAgentSubmissionError, SAP_ARCHITECTURES, STEP } from '../../create-agent.utils';
import type { BaseAgentDetailsProps } from '../../create-agent.utils';
import {
  standaloneAgentDetailsSchema,
  STANDALONE_AGENT_DETAILS_DEFAULTS,
} from '../general/generic-agent-details.schema';
import type { StandaloneAgentDetailsFormValues } from '../general/generic-agent-details.schema';
import { GenericAgentDetailsForm } from '../general/GenericAgentDetails.form';

export const StandaloneAgentDetails: FC<BaseAgentDetailsProps> = ({ onGenerateSuccess, onClose }) => {
  const [createStandaloneAgentPackageLink, { isLoading, reset }] = useCreateStandaloneAgentPackageLinkMutation();

  const form = useForm<StandaloneAgentDetailsFormValues>({
    resolver: zodResolver(standaloneAgentDetailsSchema),
    defaultValues: STANDALONE_AGENT_DETAILS_DEFAULTS,
  });

  const onGeneratePackageLink = async (data: StandaloneAgentDetailsFormValues) => {
    try {
      const standaloneAgentPayload = {
        name: data.name,
        architecture: data.architecture,
        agentHost: data.agentHost,
        ...cleanAgentDescription(data.description),
      };

      const response = await createStandaloneAgentPackageLink(standaloneAgentPayload).unwrap();
      onGenerateSuccess(response, standaloneAgentPayload, STEP.STANDALONE_AGENT_GENERATE_BUNDLE);
    } catch (error) {
      handleAgentSubmissionError(error, (msg) => form.setError('name', { type: 'server', message: msg }));
    }
  };

  const handleCloseModal = () => {
    reset();
    onClose();
  };

  return (
    <GenericAgentDetailsForm
      form={form}
      onGeneratePackageLink={onGeneratePackageLink}
      isLoading={isLoading}
      onClose={handleCloseModal}
    >
      <FormField
        control={form.control}
        name="architecture"
        render={({ field }) => {
          const isFieldDisabled = true;
          const groupLabelId = 'architecture-group-label';

          return (
            <FormItem>
              <div className="text-sm font-semibold tracking-tight text-foreground mb-1.5 block select-none">
                Architecture
              </div>

              <FormControl>
                <RadioGroup
                  aria-labelledby={groupLabelId}
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={isFieldDisabled}
                  className="flex flex-col gap-1.5 pt-0.5"
                >
                  {SAP_ARCHITECTURES.map((arch) => (
                    <div key={arch} className="flex items-center space-x-3">
                      <FormControl>
                        <RadioGroupItem value={arch} id={`arch-${arch}`} />
                      </FormControl>
                      <FormLabel
                        htmlFor={`arch-${arch}`}
                        className={cn(
                          'text-sm font-normal text-muted-foreground pl-1',
                          isFieldDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                        )}
                      >
                        {arch}
                      </FormLabel>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </GenericAgentDetailsForm>
  );
};
